import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from './UserContext';
import api from './api/axiosInstance.js';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Card from 'react-bootstrap/Card';
import { Row, Col, Container, Modal, Image } from 'react-bootstrap'; // ✅ Added Modal
import Sidebar from './Sidebar.js';
import Breadcrumb from './Breadcrumb';
import CompanyAddGeneral from './CompanyAddGeneral';
import CompanyAddHr from './CompanyAddHr';
import CompanyAddJobs from './CompanyAddJobs';
import CompanyAddJobNumbers from './CompanyAddJobNumbers';
import CompanyAddAreasDirections from './CompanyAddAreasDirections';
import { isAdminType} from './utils/roleUtils';
import icSucess from './assets/img/ic-celebration-circle.svg';
import NavTabs from './NavTabs';
import Alert from 'react-bootstrap/Alert';
import { exportCompanyPdf } from './api/exportCompanyPdf.js';

const CompanyEditHiring = () => {
    const { id, cityId } = useParams();
    const location = useLocation();
    const { user } = useContext(UserContext);
    const [cityName, setCityName] = useState('');
    useEffect(() => {
        if (!cityId) return;
        api.get(`/api/cities/${cityId}`)
            .then(res => setCityName(res.data?.name || ''))
            .catch(err => console.error('Error fetching city for breadcrumb:', err));
    }, [cityId]);
    const [activityStatus, setActivityStatus] = useState(true); // ✅ Added state
    const [showStatusModal, setShowStatusModal] = useState(false); // ✅ Added modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Delete modal state
    const [statusMessage, setStatusMessage] = useState(''); // ✅ Added message state
    const [logoPreview, setLogoPreview] = useState(''); // what’s shown as preview
    const [originalLogoUrl, setOriginalLogoUrl] = useState(''); // saved in DB
    const [forceRerenderKey, setForceRerenderKey] = useState(0);
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [savedModalTitle, setSavedModalTitle] = useState('Update successful');
    const [savedModalMessage, setSavedModalMessage] = useState('General company information was updated successfully.');
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    const cityBasePath = cityId
        ? location.pathname.includes('/city-edit-companies/')
            ? `/city-edit-companies/${cityId}/company/:id`
            : `/city-edit-jobs/${cityId}/company/:id`
        : null;
    const companyNavItems = cityBasePath
        ? [
            { to: cityBasePath, eventKey: 'companyInfo', label: 'Company Info', end: true },
            { to: `${cityBasePath}/hiring`, eventKey: 'hiringInfo', label: 'Hiring Info' },
            { to: `${cityBasePath}/users`, eventKey: 'users', label: 'Users' },
            { to: `${cityBasePath}/history`, eventKey: 'history', label: 'History of changes' },
        ]
        : [
            { to: '/company-edit/:id', eventKey: 'companyInfo', label: 'Company Info' },
            { to: '/company-edit-hiring/:id', eventKey: 'hiringInfo', label: 'Hiring Info' },
            { to: '/company-edit-users/:id', eventKey: 'users', label: 'Users' },
            { to: '/company-edit-history/:id', eventKey: 'history', label: 'History of changes' },
        ];
    
    const handleDeleteCompany = () => {
        setShowDeleteModal(true);
    };

    const confirmDeleteCompany = async () => {
        try {
            await api.delete(
                `/api/companies/${id}${id}`
            );
            setShowDeleteModal(false);
            navigate('/company-list'); // Redirect to company list after deletion
        } catch (error) {
            console.error('Error deleting company:', error);
            alert('An error occurred while deleting the company.');
        }
    };

    const [generalData, setGeneralData] = useState({
        logo: '',
        logoPreview: '',
        title: '',
        bestPlacetoWorkRating: '',
        accessLevel: '',
        url: '',
        bestPlacetoWorkDescription: '',
    });

    const [hrData, setHrData] = useState({
        hrFullName: '',
        hrRecrutingUrl: '',
        hrEmail: '',
        hrPhone: ''
    });

    const [jobsData, setJobsData] = useState({
        openedStudent: false,
        studentJobAreas: '',
        openedWorldTraveler: false,
        worldTravelerAreas: '',
        openedEnglishSpeaking: false,
        enSpeakingAreas: '',
        address: '',
        city: '',
    });

    const [jobNumbersData, setJobNumbersData] = useState({
        openJobsStudent: '',
        openJobsIntern:  '',
        openJobsStarting: '',
        openJobs13:  '',
        openJobs3: '',
        openJobsTotal: '',
    });

    const [areasData, setAreasData] = useState({
        studyFields: [
            {
            studyField: '',
            preferredStudyDirections: '',
            specialistAreas: '',
            },
        ],
    });
    
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const [logo, setLogo] = useState(null); // Store the logo file
    
    useEffect(() => {
        // Fetch company data for editing
        const fetchCompanyData = async () => {
            try {
                const response = 
                    await api.get(
                        `/api/companies/${id}`,
                    );

                const companyData = response.data;

                setActivityStatus(companyData.activityStatus ?? false); // ✅ Load activity status

                setGeneralData({
                    title: companyData.title || '',
                    url: companyData.url || '',
                    logo: companyData.logo || '',
                    logoPreview: companyData.logo || '',
                    accessLevel: companyData.accessLevel || '',
                    bestPlacetoWorkRating: companyData.bestPlacetoWorkRating || '',
                    bestPlacetoWorkDescription: companyData.bestPlacetoWorkDescription || '',
                    activityStatus: companyData.activityStatus ?? false,
                    createdAt: companyData.created_at || '',
                    updatedAt: companyData.updatedAt || '',
                    accountApprovalStatus: companyData.accountApprovalStatus || '',
                });

                setHrData({
                    hrFullName: companyData.hrFullName || '',
                    hrRecrutingUrl: companyData.hrRecrutingUrl || '',
                    hrEmail: companyData.hrEmail || '',
                    hrPhone: companyData.hrPhone || '',
                });

                setJobsData({
                    openedStudent: companyData.openedStudent || '',
                    studentJobAreas: companyData.studentJobAreas || '',
                    openedWorldTraveler: companyData.openedWorldTraveler || '',
                    worldTravelerAreas: companyData.worldTravelerAreas || '',
                    openedEnglishSpeaking: companyData.openedEnglishSpeaking || '',
                    enSpeakingAreas: companyData.enSpeakingAreas || '',
                    address: companyData.address || '',
                    city: companyData.city || '',
                });

                setJobNumbersData({
                    openJobsStudent: companyData.openJobsStudent || '',
                    openJobsIntern: companyData.openJobsIntern || '',
                    openJobsStarting: companyData.openJobsStarting || '',
                    openJobs13: companyData.openJobs13 || '',
                    openJobs3: companyData.openJobs3 || ''
                });

                const sf = companyData.studyFields;
                const normalizedStudyFields =
                    Array.isArray(sf) && sf.length > 0
                        ? sf.map(f => ({
                            studyField: f.studyField || '',
                            preferredStudyDirections: f.preferredStudyDirections || '',
                            specialistAreas: f.specialistAreas || '',
                        }))
                        : sf && typeof sf === 'object'
                        ? [{ studyField: sf.studyField || '', preferredStudyDirections: sf.preferredStudyDirections || '', specialistAreas: sf.specialistAreas || '' }]
                        : [{ studyField: '', preferredStudyDirections: '', specialistAreas: '' }];

                setAreasData({ studyFields: normalizedStudyFields });

            } catch (error) {
                console.error('Error fetching company data:', error);
                alert('An error occurred while fetching company details.');
            }
        };

        fetchCompanyData();
    }, [id]);

    const handleGeneralChange = (e) => {
        const { name, value } = e.target;

        // ✅ Handle file separately
        if (name === 'logo' && value instanceof File) {
            setLogo(value);
            const fileUrl = URL.createObjectURL(value);
            setLogoPreview(fileUrl);

            setGeneralData((prevData) => ({
                ...prevData,
                logo: value,
                logoPreview: fileUrl,
            }));
            return;
        }

        setGeneralData((prevData) => ({
            ...prevData,
            [name]: value,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleHrChange = (e) => {
        const { name, value } = e.target;

        setHrData((prevData) => ({
            ...prevData,
            [name]: value, // ✅ Ensure updates apply to hrData properly
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleJobsChange = (e) => {
        const { name, type, value, checked } = e.target;
    
        setJobsData((prevData) => {
            let updatedData = {
                ...prevData,
                [name]: type === 'checkbox' ? checked : value,
            };
    
            // ✅ If checkboxes are unchecked, clear related text fields
            if (type === 'checkbox' && !checked) {
                switch (name) {
                    case 'openedStudent':
                        console.log('🛑 Clearing studentJobAreas since openedStudent is unchecked.');
                        updatedData = { ...updatedData, studentJobAreas: '' };
                        break;
                    case 'openedWorldTraveler':
                        console.log('🛑 Clearing worldTravelerAreas since openedWorldTraveler is unchecked.');
                        updatedData = { ...updatedData, worldTravelerAreas: '' };
                        break;
                    case 'openedEnglishSpeaking':
                        console.log('🛑 Clearing enSpeakingAreas since openedEnglishSpeaking is unchecked.');
                        updatedData = { ...updatedData, enSpeakingAreas: '' };
                        break;
                    default:
                        break;
                }
            }
    
            return updatedData;
        });
    
        // ✅ Force re-render to persist cleared fields
        setTimeout(() => {
            setJobsData((prevData) => ({ ...prevData })); 
        }, 0);
    };

    const handleJobNumbersChange = (e) => {
        const { name, value } = e.target;

        setJobNumbersData((prevData) => {
            const updatedData = {
                ...prevData,
                [name]: value,
            };

            // Recalculate total dynamically
            updatedData.openJobsTotal =
                (parseInt(updatedData.openJobsStudent) || 0) +
                (parseInt(updatedData.openJobsIntern) || 0) +
                (parseInt(updatedData.openJobsStarting) || 0) +
                (parseInt(updatedData.openJobs13) || 0) +
                (parseInt(updatedData.openJobs3) || 0);

            return updatedData;
        });

        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleAreasChange = (index, field, value) => {
        setAreasData((prev) => {
            const updated = [...prev.studyFields];
            updated[index] = { ...updated[index], [field]: value }; // ✅ no mutation

            return {
                ...prev,
                studyFields: updated,
            };
        });
    };


    const validateGeneralFields = (inputURL) => {
        const newErrors = {};

        if (!generalData.title.trim()) {
            newErrors.title = 'Company name is required.';
        }

        if (!inputURL) {
            newErrors.url = 'Company website is required.';
        } else {
            const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
            if (!urlPattern.test(inputURL)) {
                newErrors.url = 'Please enter a valid URL.';
            }
        }

        setErrors(newErrors);
        console.log('Validation Errors:', newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();

        const form = e.target.closest('form'); // ✅ get the form
        const urlInput = form.querySelector('[name="url"]'); // ✅ get url input
        const inputURL = urlInput ? urlInput.value.trim() : ''; // ✅ get trimmed value

        const isValid = validateGeneralFields(inputURL);
    
        if (!isValid) {
            console.log('Validation failed, preventing submit');
            return false;
        }

        try {
            const data = new FormData();
            Object.keys(generalData).forEach((key) => {
                if (key === 'logo') {
                    if (logo) {
                        data.append('logo', logo);
                    }
                } else {
                    data.append(key, generalData[key]);
                }
            });

            const res = await api.put(
                `/api/companies/${id}/general`,
                data
            );

            const updated = res.data;
            setGeneralData((prev) => ({
                ...prev,
                ...updated,
                logoPreview: updated.logo || '',
            }));
            setLogoPreview(updated.logo || '');
            setLogo(null);
            setSavedModalTitle('Update successful');
            setSavedModalMessage('General company information was updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (error) {
            console.error('Error updating general information:', error);
            alert('An error occurred while updating general information.');
            return false;
        }
    };

    const handleHrSubmit = async (e, overrideData = {}) => {
        if (e) e.preventDefault();

        const payload = { ...hrData, ...overrideData };

        try {
            await api.put(
                `/api/companies/${id}/hr`,
                payload
            );

            setHrData(payload);
            setSavedModalTitle('Update successful');
            setSavedModalMessage('Hiring info updated successfully.');
            setShowSavedModal(true);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.msg) {
                // Display the error message from the backend
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    vat: error.response.data.msg,
                }));
            } else {
                console.error('Error updating HR information:', error);
                alert('An error occurred while updating HR information.');
            }
        }
    };

    const handleJobsSubmit = async (e) => {
        e.preventDefault();
    
        try {
            await api.put(
                `/api/companies/${id}/jobs`,
                jobsData,
            );
            
            setSavedModalTitle('Update successful');
            setSavedModalMessage('Job preferences updated successfully.');
            setShowSavedModal(true);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.msg) {
                // Display the error message from the backend
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    vat: error.response.data.msg,
                }));
            } else {
                console.error('Error updating jobs information:', error);
                alert('An error occurred while updating jobs information.');
            }
        }
    };

    const handleAreasSubmit = async (e) => {
        e.preventDefault();
        console.log('🚀 Submitting studyFields:', areasData.studyFields); // ✅
        try {
            await api.put(
                `/api/companies/${id}/areas`,
                { studyFields: areasData.studyFields },
            );
            setSavedModalTitle('Update successful');
            setSavedModalMessage('Specialist areas & study directions updated successfully.');
            setShowSavedModal(true);
        } catch (error) {
            console.error('❌ Axios error:', error.response?.data || error);

            if (error.response?.data?.msg) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    vat: error.response.data.msg,
                }));
            } else {
                alert('An error occurred while updating Specialist Areas.');
            }
        }
    };

    const handleJobNumbersSubmit = async (e) => {
        e.preventDefault();
        console.log('Submitting job numbers:', jobNumbersData); // Debugging
    
        try {

            await api.put(
                `/api/companies/${id}/job-numbers`,
                jobNumbersData,
            );
            
            setSavedModalTitle('Update successful');
            setSavedModalMessage('Number of jobs updated successfully.');
            setShowSavedModal(true);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.msg) {
                // Display the error message from the backend
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    vat: error.response.data.msg,
                }));
            } else {
                console.error('Error updating job numbers information:', error);
                alert('An error occurred while updating job numbers information.');
            }
        }
    };

    if (!user || !isAdminType(user.role)) {
        return <div>You are not authorized to view this page.</div>;
    }

    const toggleActivityStatus = async () => { // ✅ Function to toggle activity status
        try {
            const updatedStatus = !activityStatus;
            await api.patch(
                `/api/companies/${id}/activity-status`,
                { activityStatus: updatedStatus },
            );

            setActivityStatus(updatedStatus);
            
            // ✅ Ensure `generalData` gets the correct updated status
            setGeneralData((prevData) => ({
                ...prevData,
                activityStatus: updatedStatus, // ✅ Update status in generalData
            }));

            setStatusMessage(updatedStatus ? "Company Activated" : "Company Deactivated"); // ✅ Update message
            setShowStatusModal(true); // ✅ Show modal
            

        } catch (error) {
            console.error('Error toggling activity status:', error);
            alert('An error occurred while updating the activity status.');
        }
    };

    const handleResetLogo = () => {
        setLogo(null);
        setLogoPreview(originalLogoUrl); // restore saved logo from DB
    };

    const handleDeleteImage = () => {
        setLogo(null);
        setLogoPreview('fallback'); // we'll handle it in the child
        setGeneralData((prev) => ({
            ...prev,
            logo: '',
            logoPreview: 'fallback',
        }));
    };

    const handleExportPdf = async () => {
        try {
            await exportCompanyPdf(id);
        } catch (error) {
            console.error('Error exporting company PDF:', error);
            alert('An error occurred while exporting the company PDF.');
        }
    };

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1">
                <Container>
                    <Row>
                        <Col>
                            {showErrorMessage && (
                                <Alert variant="danger" onClose={() => setShowErrorMessage(false)} dismissible>
                                    <p><strong>Error</strong> - There are items that require your attention</p>
                                </Alert>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Breadcrumb cityName={cityName} companyName={generalData.title} />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body className="pt-0">
                                    <Row>
                                        <Col xs={12}>
                                            <>
                                                <Row>
                                                    <Col>
                                                        <Form onSubmit={handleGeneralSubmit}>
                                                            <CompanyAddGeneral
                                                                formData={generalData}
                                                                handleChange={handleGeneralChange}
                                                                logo={generalData.logoPreview}
                                                                errors={errors}
                                                                handleGeneralSubmit={handleGeneralSubmit}
                                                                isEditPage={true}
                                                                activityStatus={activityStatus}
                                                                handleResetLogo={handleResetLogo}
                                                                forceRerenderKey={forceRerenderKey}
                                                                setForceRerenderKey={setForceRerenderKey}
                                                                handleDeleteImage={handleDeleteImage}
                                                                showErrorMessage={showErrorMessage}
                                                                setShowErrorMessage={setShowErrorMessage}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col>
                                                        <NavTabs items={companyNavItems} />
                                                    </Col>
                                                </Row>
                                                <Row className="mb-5">
                                                    <Col><h2>Hiring info</h2></Col>
                                                </Row>
                                                <Row className="mb-4">
                                                    <Col md={12}>
                                                        <Form onSubmit={handleHrSubmit}>
                                                            <CompanyAddHr
                                                                formData={hrData}
                                                                handleChange={handleHrChange}
                                                                errors={errors}
                                                                isEditPage={true} // ✅ Pass `isEditPage=true`
                                                                handleHrSubmit={handleHrSubmit}
                                                                handleDeleteImage={handleDeleteImage}
                                                                showErrorMessage={showErrorMessage}
                                                                setShowErrorMessage={setShowErrorMessage}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-4">
                                                    <Col md={12}>
                                                        <Form onSubmit={handleJobsSubmit}>
                                                            <CompanyAddJobs
                                                                formData={jobsData}
                                                                handleChange={handleJobsChange}
                                                                errors={errors}
                                                                isEditPage={true} // ✅ Pass `isEditPage=true`
                                                                handleJobsSubmit={handleJobsSubmit}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                                <Row className="mb-4">
                                                    <Col md={12}>
                                                        <Form onSubmit={handleJobNumbersSubmit}>
                                                            <CompanyAddJobNumbers
                                                                formData={jobNumbersData}
                                                                handleChange={handleJobNumbersChange}
                                                                errors={errors}
                                                                isEditPage={true} // ✅ Pass `isEditPage=true`
                                                                handleJobNumbersSubmit={handleJobNumbersSubmit}
                                                            />
                                                        </Form>
                                                    </Col>
                                                </Row>
                                            </>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={12}>
                                            <Form onSubmit={handleAreasSubmit}>
                                                <CompanyAddAreasDirections
                                                    formData={areasData}
                                                    handleChange={handleAreasChange}
                                                    errors={errors}
                                                    isEditPage={true} // ✅ Pass `isEditPage=true`
                                                    handleAreasSubmit={handleAreasSubmit}
                                                    setFormData={setAreasData}
                                                />
                                            </Form>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>  
                        </Col>
                    </Row>
                    <Row className="buttons-row">
                        <Col>
                            <Button
                                variant="outline-danger"
                                onClick={handleDeleteCompany} 
                                className="px-4"
                            >
                                    Delete
                            </Button>
                            <Button 
                                variant="outline-secondary"
                                onClick={toggleActivityStatus} 
                                className="px-4"
                            >
                                {activityStatus ? "Deactivate" : "Activate"}
                            </Button>
                            <Button variant="outline-secondary" onClick={handleExportPdf}>
                                Download company report
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </main>

            {/* ✅ Activity Status Modal */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Status Update</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{statusMessage}</p> {/* ✅ Show message */}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowStatusModal(false)}>OK</Button> {/* ✅ Close modal */}
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this company? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary me-3"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={confirmDeleteCompany} 
                        className=""
                    >
                            Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Saved info Modal */}
            <Modal show={showSavedModal} onHide={() => setShowSavedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">{savedModalTitle}</h2>
                    <p className="text-center">{savedModalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowSavedModal(false)}>OK</Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default CompanyEditHiring;
